<template>
  <BContainer
    class="mb-4 px-0"
  >
    <BRow
      class="pb-2"
    >

      <BCol
        cols="12"
        md="6"
      >
        <ValidationObserver
          ref="formRegion"
        >
          <form
            role="form"
            novalidate
            @submit.prevent
          >
            <ValidationProvider
              v-slot="{ errors }"
              vid="region_id"
              name="region"
              rules="required"
            >
              <BFormGroup
                label="Region"
                label-for="fRegion"
                label-class="font-weight-bolder"
                class="mb-2"
              >
                <VSelect
                  v-model="selected.region"
                  input-id="fRegion"
                  label="region_name"
                  append-to-body
                  placeholder="select region here"
                  :options="filteredRegions"
                  :disabled="fetching.regions"
                  :loading="fetching.regions"
                  :class="{ 'is-invalid': errors.length > 0 }"
                >
                  <template #selected-option="{ region_name }">
                    <div
                      class="w-225px w-lg-325px text-truncate"
                      v-text="region_name"
                    />
                  </template>
                  <template #no-options="">
                    no available region
                  </template>
                </VSelect>
                <div
                  v-if="(errors.length > 0)"
                  class="invalid-feedback"
                  v-text="errors[0]"
                />
              </BFormGroup>
            </ValidationProvider>
          </form>
        </ValidationObserver>
      </BCol>

      <BCol>
        <BFormGroup
          class="mb-2"
        >
          <label
            for="actionButtons"
            class="d-none d-md-block"
          >
            &nbsp;
          </label>
          <BButton
            variant="primary"
            :disabled="state.busy || !selected.region"
            @click="validateRegion"
          >
            Add Region
          </BButton>
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
      >
        <BFormGroup
          label="Search"
          label-for="filterRegionSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterRegionSearch"
            v-model="tableRegions.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>
    </BRow>

    <BTable
      ref="tableRegions"
      small
      hover
      bordered
      responsive
      show-empty
      :items="tableProvider"
      :filter="tableRegions.filter"
      :stacked="tableRegions.stacked"
      :fields="tableRegions.fields"
    >
      <template #cell(index)="{ index }">
        {{ index + 1 }}
      </template>
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center gap-2">
          <BButton
            size="sm"
            variant="danger"
            @click.prevent="deleteRegion(item)"
          >
            Delete
          </BButton>
        </div>
      </template>
    </BTable>
  </BContainer>
</template>

<script>
import QUERY from 'lodash'
import { fuseFilter } from '@/plugins/fuse'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MRegions from '@/mixins/locations/regions'

export default {
  name: 'VAdminUserLocationsRegions',

  mixins: [
    MxMisc,
    MxFormatter,
    MRegions
  ],

  middleware: [
    'auth',
    'admin'
  ],

  props: {
    regions: {
      type: Array,
      required: true,
      default: () => []
    }
  },

  emits: ['updated'],

  data () {
    return {
      state: {
        busy: false,
        editing: false
      },
      formRegion: {
        id: '',
        region_code: '',
        region_name: ''
      },
      tableRegions: {
        busy: false,
        filter: {
          search: ''
        },
        filterOn: [],
        stacked: 'sm',
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        rows: this.regions,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 4, key: 'action', class: 'text-center w-md-125px' },
          { mobile: 1, key: 'region_name', class: 'min-w-175px' },
          { mobile: 2, key: 'region.region_name', label: 'Region', class: 'min-w-175px' }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: {
    filteredRegions () {
      return this.options.regions.filter(
        ({ id }) => !Array.from(this.tableRegions.rows).some(
          region => Number(region.id) === Number(id)
        )
      )
    }
  },

  watch: {
    'selected.region' (region) {
      this.formRegion.id = region?.id || ''
      this.formRegion.region_code = region?.region_code || ''
      this.formRegion.region_name = region?.region_name || ''
    }
  },

  mounted () {
    this.getRegions(this)
  },

  methods: {

    async tableProvider ({ filter }) {
      return fuseFilter(filter.search, this.tableRegions.rows, {
        keys: ['region_name']
      })
    },

    clearRegion () {
      this.state.editing = false

      this.selected.region = ''

      this.formRegion = {
        id: '',
        region_code: '',
        region_name: ''
      }

      this.$refs.formRegion.reset()
    },

    editRegion (region) {
      this.state.editing = true

      this.formRegion = Object.assign(this.formRegion, {
        id: region.id,
        region_code: region.region_code,
        region_name: region.region_name
      })

      this.selected.region = QUERY.find(this.options.regions, {
        id: region.id
      })
    },

    validateRegion () {
      this.$refs.formRegion.validate().then(
        async validRegion => {
          if (!validRegion) {
            return this.swalInvalid()
          }

          const regionExists = QUERY.find(this.tableRegions.rows, {
            id: this.formRegion.id
          })

          if (!regionExists) {
            this.tableRegions.rows.push(this.formRegion)
          }

          this.clearRegion()
          this.refetch()
        }
      )
    },

    deleteRegion (region) {
      this.tableRegions.rows = QUERY.filter(this.tableRegions.rows,
        ({ id }) => Number(id) !== Number(region.id)
      )

      this.refetch()
    },

    refetch () {
      this.$emit('updated', this.tableRegions.rows)

      this.$refs.tableRegions.refresh()
    }
  }
}
</script>
